import type { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, concatMap, map, mergeMap, of } from 'rxjs';

import { createEffectFactory } from '@recruitee/ngrx';

import { ViewsHttpService } from '../../services/views-http.service';
import { InputOptionsActions } from './input-options.store';

@Injectable({ providedIn: 'root' })
export class InputOptionsEffects {
  public effect = createEffectFactory(InputOptionsActions, this.actions$);

  public fetchOne$ = this.effect(
    InputOptionsActions.fetchOne,
    ({ payload: { dataSourceId, filterId, inputId, inputOptionsId, queryParams, filterValue } }) =>
      this.viewsHttpService
        .fetchInputOptions(dataSourceId, filterId, inputId, filterValue, queryParams)
        .pipe(
          map(data => ({
            data: {
              id: inputOptionsId,
              ...data,
            },
          })),
        ),
    { customMapOperator: mergeMap },
  );

  public fetchMore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InputOptionsActions.fetchMore),
      concatMap(
        ({
          payload: { dataSourceId, filterId, inputId, inputOptionsId, queryParams, filterValue },
        }) =>
          this.viewsHttpService
            .fetchInputOptions(dataSourceId, filterId, inputId, filterValue, queryParams)
            .pipe(
              map(data =>
                InputOptionsActions.fetchMoreSuccess({
                  payload: {
                    data: {
                      id: inputOptionsId,
                      ...data,
                    },
                  },
                }),
              ),
              catchError((error: HttpErrorResponse) =>
                of(InputOptionsActions.fetchMoreFailed({ payload: { error, id: inputOptionsId } })),
              ),
            ),
      ),
    ),
  );

  public validate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InputOptionsActions.validate),
      mergeMap(({ payload: { dataSourceId, filterId, inputId, inputOptionsId, filterValue } }) =>
        this.viewsHttpService.fetchInputOptions(dataSourceId, filterId, inputId, filterValue).pipe(
          map(data =>
            InputOptionsActions.validateSuccess({
              payload: {
                data: {
                  id: inputOptionsId,
                  ...data,
                },
              },
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(InputOptionsActions.validateFailed({ payload: { error, id: inputOptionsId } })),
          ),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private viewsHttpService: ViewsHttpService,
  ) {}
}
